<template>
  <teleport to="body">
    <dialog
      ref="dialog"
      v-on-click-outside="onClickOutside"
      closedby="none"
      class="m-auto flex overflow-hidden outline-none"
      aria-modal="true"
      v-bind="$attrs"
    >
      <div v-if="modal.open.value" class="overflow-y-auto bg-white shadow-md" :class="[sizeClass, layout, borderRadius]">
        <slot name="header">
          <gv-modal-header :modal="modal" :title="title" />
        </slot>
        <slot />
        <slot name="footer" />
      </div>
    </dialog>
  </teleport>
</template>

<script lang="ts" setup>
import { PropType, computed, onMounted, useTemplateRef, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

import { UseModal } from '@/common/composables/useModal';
import { Size } from './types';
import { usePoller } from '@/common/composables';
import { Poller } from '@/features';
import { tw } from '@/common';

const emit = defineEmits(['open', 'close']);

const dialog = useTemplateRef('dialog');

const props = defineProps({
  modal: {
    type: Object as PropType<UseModal>,
    required: true,
  },
  size: {
    type: String as PropType<Size>,
    default: 'md',
  },
  title: {
    type: String,
  },
  customSizeClass: {
    type: String,
  },
  closeOnClickOutside: {
    type: Boolean,
    default: true,
  },
  layout: {
    type: String,
    default: 'default',
  },
  borderRadius: {
    type: String,
    default: 'rounded-lg',
  },
  ariaLabelledby: {
    type: String,
    default: undefined,
  },
});

const poller = usePoller();

const sizeClasses: Record<Size, string> = {
  sm: tw`w-[22.5rem]`,
  md: tw`w-[30rem]`,
  lg: tw`w-[34.5rem]`,
  xl: tw`mx-8 h-full`,
  custom: '',
};

const sizeClass = computed(() => sizeClasses[props.size] || props.customSizeClass);

const layout = computed(() => {
  switch (props.layout) {
    case 'flex-row':
      return 'flex flex-row';
    case 'flex-col':
      return 'flex flex-col';
    case 'default':
    default:
      return '';
  }
});

watch(props.modal.open, () => {
  emit(props.modal.open.value ? 'open' : 'close');

  if (props.modal.open.value) {
    dialog.value?.showModal();
    poller.changeInterval(Poller.fetchAllHazards, 3 * 60 * 1000); // 3 minutes
  } else {
    dialog.value?.close();
    poller.changeInterval(Poller.fetchAllHazards, 60 * 1000); // 1 minute
  }

  poller.startPoller(Poller.fetchAllHazards);
});

const onClickOutside = () => {
  if (!props.closeOnClickOutside) return;
  props.modal.setClosed();
};

onMounted(() => {
  if (props.modal.open.value) {
    dialog.value?.showModal();
  }
});
</script>
<style scoped>
::backdrop {
  background-color: rgba(0, 0, 0, 0.25);
}

dialog {
  background: none;
}

dialog {
  opacity: 0;
  transform: scale(0.95);
  transition:
    opacity 150ms ease,
    transform 150ms ease;
}

dialog[open] {
  opacity: 1;
  transform: scale(1);
}

dialog::backdrop {
  opacity: 0;
  transition: opacity 150ms ease;
}

dialog[open]::backdrop {
  opacity: 1;
}
</style>
